/* eslint no-await-in-loop: off */
/* Libs */
import create from 'zustand';
import { formatISO } from 'date-fns';

import { apiSnackBarStore } from 'Stores/SnackBar';
/* Model */
import Orders from 'Models/Orders';
import Etsy from 'Models/Etsy';
import Squarespace from 'Models/Squarespace';

const [useOrdersStore] = create((set, get) => ({
	orders: {},
	fetchingOrders: false,
	fetchingSyncOrders: false,
	ordersCount: {
		new: 0,
		pending: 0,
		sending: 0,
		sent: 0
	},
	sendingOrder: false,
	addingNote: false,

	getLastSync: async () => {
		const response = await Orders.getLastSync();
		set({ lastSync: response });
	},

	syncOrders: async (params) => {
		set({ fetchingSyncOrders: true });
		await Orders.sync(params);
		window.location.reload();
	},

	searchOrders: async (query) => {
		const response = await Orders.search(query);
		console.warn('response from searchOrders', response);
		return response;
	},

	fetchOrders: async (sort) => {
		const { orders } = get();
		const params = { status: sort };

		if (orders[sort] && !orders[sort].more) return;

		set({ fetchingOrders: true });

		const response = await Orders.fetch(params);

		if (sort === 'sent') {
			const responseCanceled = await Orders.fetch({ status: 'canceled' });
			response.datas = [...response.datas, ...responseCanceled.datas];
		}

		const newOrders = { ...orders };
		const newOrdersSorted = newOrders[sort] || { more: false, datas: [] };

		newOrdersSorted.more = response.more || false;
		newOrdersSorted.datas = [
			...newOrdersSorted.datas.filter(order => !response.datas.find(o => o.id === order.id)),
			...response.datas
		];
		newOrders[sort] = newOrdersSorted;

		set({ orders: newOrders, fetchingOrders: false });
	},

	sentOrder: async (order, params) => {
		console.log('sent order params', order, params);
		const { showSnackBar } = apiSnackBarStore.getState();

		set({ sendingOrder: true });
		let response = null;

		if (order.source === 'etsy') {
			response = await Etsy.sentOrder(params);
		} else {
			response = await Squarespace.sentOrder(params);
		}

		set({ sendingOrder: false });
		if (response.error) {
			showSnackBar({ theme: 'danger', message: 'Problème lors de l\'envoi de la commande' });
			return false;
		}

		showSnackBar({ theme: 'success', message: 'Commande expédiée ! Un email a été envoyé au client.' });
		return true;
	},

	addNote: async ({ order, note }) => {
		console.log('add/edit note order params', order, note);
		const { showSnackBar } = apiSnackBarStore.getState();

		set({ addingNote: true });
		const newInternalNotes = order.details.internalNotes || [];
		newInternalNotes[0] = { content: note };

		const newDetails = {
			...order.details,
			internalNotes: newInternalNotes
		};
		const response = await Orders.update(order.id, { details: newDetails });

		set({ addingNote: false });
		if (response.error) {
			showSnackBar({ theme: 'danger', message: 'Problème lors de l\'ajout de la note' });
			return false;
		}

		showSnackBar({ theme: 'success', message: 'Note ajoutée.' });
		return true;
	},

	deleteNote: async (order) => {
		console.log('delete note order params', order);
		const { showSnackBar } = apiSnackBarStore.getState();

		order.details.internalNotes = [];
		const newDetails = { ...order.details };
		const response = await Orders.update(order.id, { details: newDetails });

		if (response.error) {
			showSnackBar({ theme: 'danger', message: 'Problème lors de la suppression de la note' });
			return false;
		}

		showSnackBar({ theme: 'success', message: 'Note supprimée.' });
		return true;
	},

	getOrdersCount: async () => {
		const ordersNew = await Orders.getCount({ status: 'new' });
		const ordersPending = await Orders.getCount({ status: 'pending' });
		const ordersSending = await Orders.getCount({ status: 'sending' });
		const ordersSent = await Orders.getCount({ status: 'sent' });
		const ordersCanceled = await Orders.getCount({ status: 'canceled' });

		const ordersCount = {
			new: ordersNew,
			pending: ordersPending,
			sending: ordersSending,
			sent: ordersSent + ordersCanceled
		};

		set({ ordersCount });
	},

	updateStatus: async (order, status, trackings = null) => {
		const { orders, getOrdersCount } = get();
		const { showSnackBar } = apiSnackBarStore.getState();
		const params = { status };

		if (status === 'canceled') {
			params.modified_on = formatISO(new Date());
		}

		const response = await Orders.update(order.id, { status });
		if (response) {
			const oldStatus = `${order.status}`;
			order.status = status;

			if (status === 'canceled') {
				status = 'sent';
			}

			if (status === 'sent' && trackings) {
				order.details.trackings = trackings;
			}

			let newOrdersStatus = orders[status] || { more: true, datas: [] };
			newOrdersStatus = {
				...newOrdersStatus,
				datas: [...newOrdersStatus.datas, order] || [order]
			};
			const newOldOrdersStatus = {
				...orders[oldStatus],
				datas: orders[oldStatus].datas.filter(o => o.id !== order.id)
			};

			const newOrders = {
				...orders,
				[oldStatus]: newOldOrdersStatus,
				[status]: newOrdersStatus
			};

			set({ orders: newOrders });
			getOrdersCount();

			if (status !== 'sent') {
				showSnackBar({ theme: 'success', message: 'Commande modifiée' });
			}
		}
	},

	updateManyStatus: async (ordersSelected, status) => {
		const { orders, getOrdersCount } = get();
		const { showSnackBar } = apiSnackBarStore.getState();
		let newOrders = { ...orders };

		for (let i = 0; i < ordersSelected.length; i += 1) {
			const order = ordersSelected[i];
			const response = await Orders.update(order.id, { status });

			if (response) {
				const oldStatus = `${order.status}`;
				order.status = status;

				let newOrdersStatus = newOrders[status] || { more: true, datas: [] };
				newOrdersStatus = {
					...newOrdersStatus,
					datas: [...newOrdersStatus.datas, order] || [order]
				};
				const newOldOrdersStatus = {
					...newOrders[oldStatus],
					datas: newOrders[oldStatus].datas.filter(o => o.id !== order.id)
				};

				newOrders = {
					...newOrders,
					[oldStatus]: newOldOrdersStatus,
					[status]: newOrdersStatus
				};
			}
		}

		set({ orders: newOrders });
		getOrdersCount();

		if (status !== 'sent') {
			showSnackBar({ theme: 'success', message: 'Commande(s) modifiée(s)' });
		}
	}
}));


export default useOrdersStore;
